/**
 * DevicePendingBanner - persistent banner shown when this device is pending approval.
 *
 * Not dismissible — stays until the device is approved or the user uses the recovery phrase.
 *
 * On approval: fetches and decrypts all workspace seeds from the auth server,
 * dispatches seed-received events so locked CRDTStorage instances unlock in-place,
 * then calls onApproved() to remove the banner — no page reload needed.
 *
 * Also listens for seed:ready signals from other devices so it can proactively
 * fetch seeds the moment they're stored, even before the device is approved.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Key } from 'lucide-react';
import { getDeviceStatus } from '../accounts/deviceService';
import { onSeedReady, startSignaling } from '@yada/sync';
import { capturePairingScope, pairingErrorMessage } from './pairingScope';
import { onBecameVisible } from '../utils/visibility';

interface DevicePendingBannerProps {
  onApproved: () => void;
  onUseRecovery: () => void;
  onPairNow?: () => void;
}

export const DevicePendingBanner: React.FC<DevicePendingBannerProps> = ({ onApproved, onUseRecovery, onPairNow }) => {
  const navigate = useNavigate();
  const [setupMessage, setSetupMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let checking = false;
    const scopeMatches = capturePairingScope();
    const deviceId = localStorage.getItem('yada:deviceId');
    if (!deviceId) return;

    // Start signaling so we receive seed:ready notifications from other devices.
    // A pending device may not have opened any workspace yet (so syncPushClient
    // hasn't started signaling), but we still need the channel for seed delivery.
    startSignaling().catch(err =>
      console.warn('[DevicePendingBanner] Signaling start failed:', err)
    );

    const checkApproval = async () => {
      if (checking || cancelled || !scopeMatches()) return;
      checking = true;
      try {
        const status = await getDeviceStatus(deviceId);
        if (cancelled || !scopeMatches()) return;
        if (status.status === 'approved') {
          setSetupMessage('Device approved. Checking encryption keys and syncing workspace data…');
          const { prefetchWorkspaceSeeds } = await import('@yada/sync');
          await prefetchWorkspaceSeeds();
          if (cancelled || !scopeMatches()) return;
          localStorage.setItem('yada:lastKnownDeviceStatus', 'approved');
          onApproved();
        }
      } catch (err) {
        if (!cancelled && scopeMatches()) setSetupMessage(pairingErrorMessage(err));
      } finally { checking = false; }
    };
    const unsubSeedReady = onSeedReady(() => { void checkApproval(); });
    const seedReceived = () => { void checkApproval(); };
    window.addEventListener('yada:workspace:seed-received', seedReceived);
    void checkApproval();

    // Listen to central update poller
    let unsubPoller: (() => void) | undefined;
    import('@yada/sync').then(({ getUpdatePoller }) => {
      const poller = getUpdatePoller();
      if (cancelled) return;
      unsubPoller = poller.on('devices:pending', (pendingDevices: Array<{ id: string }>) => {
        // If our device is no longer pending, check if it was approved
        if (!pendingDevices.some(d => d.id === deviceId)) {
          checkApproval();
        }
      });
    }).catch(() => {});

    // Approval arrives through seed:ready, seed-received and the update poller's
    // devices:pending, so there is no separate poll. Returning to the tab rechecks
    // in case a signal was missed while it was hidden.
    const offVisible = onBecameVisible(() => { void checkApproval(); });
    return () => {
      cancelled = true;
      window.removeEventListener('yada:workspace:seed-received', seedReceived);
      offVisible();
      unsubSeedReady();
      unsubPoller?.();
    };
  }, [onApproved]);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '0.625rem 1rem',
        background: 'var(--warning-bg)',
        color: 'var(--warning-text)',
        fontSize: '0.875rem',
        borderBottom: '0.125rem solid var(--warning-text)',
        gap: '0.5rem',
        flexWrap: 'wrap',
      }}
      role="alert"
      aria-live="polite"
    >
      <Clock size={15} style={{ flexShrink: 0 }} />
      <span
        style={{ flex: 1, cursor: 'pointer' }}
        onClick={() => navigate('/devices')}
      >
        <strong>{setupMessage || "This device is waiting to be linked."}</strong>
        {!setupMessage && <> Encrypted content is locked until an existing device shares the keys. </>}
        {' '}
        {onPairNow
          ? <u style={{ cursor: 'pointer' }} onClick={onPairNow}>Show pairing options →</u>
          : <u>Manage devices →</u>
        }
      </span>
      <button
        type="button"
        onClick={onUseRecovery}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.25rem',
          background: 'none',
          border: '0.0625rem solid currentColor',
          borderRadius: '0.375rem',
          color: 'inherit',
          fontSize: '0.8125rem',
          padding: '0.25rem 0.625rem',
          cursor: 'pointer',
          flexShrink: 0,
          opacity: 0.85,
        }}
        aria-label="Use recovery phrase to restore access without waiting to be linked"
      >
        <Key size={13} />
        Use recovery phrase
      </button>
    </div>
  );
};
